import {
  Background,
  BackgroundVariant,
  Controls,
  Edge,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import ErrorMessage from '@/components/errorMessage';
import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import { useUserContext } from '@/contexts/userContext';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Chip, IconButton, Tooltip, useTheme, useMediaQuery, Drawer } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import '@xyflow/react/dist/style.css';
import { useConfirm } from 'material-ui-confirm';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EntityType } from '../entities/entityType';
import ProgressIndicator from '../progressIndicator';
import LeftSidebar from './leftSidebar';
import {
  createEntityNode,
  createEventEdge,
  createSourceEntityEdge,
  getLayoutedElements,
  NodeData,
  nodeTypesMap,
} from './nodes/nodesUtils';
import PipelineInfoPanel from './pipelineInfoPanel';
import SavePipelineDialog from './savePipelineDialog';
import BlueprintSettingsDialog, { BlueprintPromptSetting } from './blueprintSettingsDialog';

interface Props {
  entity?: any;
  surveyId?: string;
  responseId?: string;
  maxHeight: number;
  isOpen: boolean;
  viewOnly?: boolean;
  onConfigurationChange: () => void;
}

interface HandlersRef {
  updateNode?: (updatedEntity: any) => Promise<void>;
  deleteNode?: (nodeId: string) => void;
  getNewEntities?: () => any[];
  getAllEntities?: () => any[];
  maxHeight: number;
  viewOnly: boolean;
  entityId?: string;
}

const isItemVisible = (item: any, settings: any) => {
  if (item.system) {
    return false;
  }
  if (!item.conditions) {
    return true;
  }
  let pass = true;
  for (const condition of item.conditions) {
    if (Array.isArray(condition.settingValue)) {
      if (!condition.settingValue.includes(settings[condition.settingName])) {
        pass = false;
        break;
      }
    } else {
      if (
        condition.settingValue === false ||
        condition.settingValue === null ||
        condition.settingValue === undefined
      ) {
        if (settings[condition.settingName]) {
          pass = false;
          break;
        }
      } else if (!(settings[condition.settingName] === condition.settingValue)) {
        pass = false;
        break;
      }
    }
  }
  return pass;
};

// Module-level persistent cache to prevent flickering on TabPanel remounts
const nodeCache: Record<string, Node<NodeData>[]> = {};
const edgeCache: Record<string, Edge[]> = {};

const ReactFlowPipelineBuilder = ({
  maxHeight,
  entity,
  surveyId,
  responseId,
  isOpen,
  viewOnly = false,
  onConfigurationChange,
}: Props) => {
  const theme = useTheme();
  const apiContext = useApiContext();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { user } = useUserContext();
  const { toast } = useNotificationContext();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchParams, setSearchParams] = useSearchParams();

  const blueprintIdParam = searchParams.get('blueprintId');
  const pipelineInstanceIdParam = searchParams.get('pipelineInstanceId');

  const currentId =
    entity?.id ||
    (surveyId && responseId ? `${surveyId}-${responseId}` : null) ||
    pipelineInstanceIdParam ||
    blueprintIdParam;

  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(true);
  const [blueprints, setBlueprints] = useState<Array<{ label: string; value: string }>>([]);
  const [fullBlueprints, setFullBlueprints] = useState<any[]>([]);

  const { getNodes, getEdges, fitView } = useReactFlow<Node<NodeData>, Edge>();

  const [changed, setChanged] = useState(false);
  const [deletedSubscriptions, setDeletedSubscriptions] = useState<any[]>([]);

  // Initialization from cache (Zero-Flicker Mount)
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeData>>(currentId ? nodeCache[currentId] || [] : []);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(currentId ? edgeCache[currentId] || [] : []);

  const [layoutElements, setLayoutElements] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadedBlueprintId, setLoadedBlueprintId] = useState<string | null>(null);
  const [error, setError] = useState<any>(null);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [pipelineInstanceId, setPipelineInstanceId] = useState<string | undefined>();
  const [loadedPipelines, setLoadedPipelines] = useState<any[]>([]);
  const [activePipeline, setActivePipeline] = useState<any | null>(null);

  const [blueprintPromptOpen, setBlueprintPromptOpen] = useState(false);
  const [promptSettings, setPromptSettings] = useState<BlueprintPromptSetting[]>([]);
  const [pendingBlueprint, setPendingBlueprint] = useState<any | null>(null);
  const [pendingEntities, setPendingEntities] = useState<Array<{ tempId: string; entity: any }>>([]);

  const nodesInitialized = useNodesInitialized();

  // Guards and late-bound handlers
  const isFetchingRef = useRef(false);
  const lastFetchedIdRef = useRef<string | null>(null);
  const blueprintLoadedRef = useRef(false);
  const handlersRef = useRef<HandlersRef>({
    maxHeight,
    viewOnly,
    entityId: entity?.id,
  });

  useEffect(() => {
    handlersRef.current = {
      ...handlersRef.current,
      maxHeight,
      viewOnly,
      entityId: entity?.id,
    };
  }, [maxHeight, viewOnly, entity?.id]);

  useEffect(() => {
    if (!user) return;
    const fetchBlueprints = async () => {
      try {
        const rawBpData = (await apiContext.getPipelineBlueprints()) as any;
        const bpData = Array.isArray(rawBpData) ? rawBpData : rawBpData?.data || [];
        setFullBlueprints(bpData);
        setBlueprints(bpData.map((t: any) => ({ label: t.name, value: t.id })));
      } catch (err) {
        console.error('Failed to load blueprints', err);
      }
    };
    fetchBlueprints();
  }, [user]);

  const deriveEdges = useCallback(
    (allNodes: Node<NodeData>[]) => {
      const newEdges: Edge[] = [];
      allNodes.forEach((n) => {
        (n.data.entity.eventSubscriptions || []).forEach((es: any) => {
          newEdges.push(createEventEdge(es, theme.palette.primary.main));
        });
        (n.data.entity.sourceEntities || []).forEach((se: any) => {
          newEdges.push(createSourceEntityEdge(se, theme.palette.primary.main));
        });
      });
      return newEdges;
    },
    [theme.palette.primary.main],
  );

  /**
   * Attaches proxy handlers to node data. These proxies point to the latest
   * handlers in handlersRef, ensuring stable references for React Flow.
   */
  const attachHandlers = useCallback((node: Node<NodeData>) => {
    node.data.currentEntityId = handlersRef.current.entityId;
    node.data.updateNode = (ent) => handlersRef.current.updateNode?.(ent);
    node.data.deleteNode = (id) => handlersRef.current.deleteNode?.(id);
    node.data.getNewEntities = () => handlersRef.current.getNewEntities?.() || [];
    node.data.getAllEntities = () => handlersRef.current.getAllEntities?.() || [];
    node.data.panelMaxHieght = handlersRef.current.maxHeight;
    node.data.viewOnly = handlersRef.current.viewOnly;
    return node;
  }, []);

  const getNewEntities = useCallback(() => {
    return getNodes()
      .filter((n) => n.data.entityNode && !n.data.entity.id)
      .map((n) => ({ id: n.id, ...n.data.entity }));
  }, [getNodes]);

  const getAllEntities = useCallback(() => {
    return getNodes()
      .filter((n) => n.data.entityNode)
      .map((n) => ({
        id: n.data.entity?.id || n.id,
        name: n.data.entity?.name || n.data.entity?.agentDef?.name || n.id,
        entityType: n.data.entity?.entityType || (n.type as any),
        entityDefId: n.data.entity?.entityDefId || n.data.entity?.agentDef?.id,
        agentDef: n.data.entity?.agentDef,
        description: n.data.entity?.description || n.data.entity?.agentDef?.description,
      }));
  }, [getNodes]);

  /**
   * Updates local graph state and updates the persistent module cache.
   */
  const updateGraphState = useCallback(
    (newNodes: Node<NodeData>[], newEdges: Edge[], incrementChanged = true) => {
      setNodes(newNodes);
      setEdges(newEdges);
      if (currentId) {
        nodeCache[currentId] = newNodes;
        edgeCache[currentId] = newEdges;
      }
      if (incrementChanged) setChanged(true);
    },
    [setNodes, setEdges, currentId],
  );

  const cleanupNodeReferences = useCallback((allNodes: Node<NodeData>[], deletedNodeId: string) => {
    const removedSubs: any[] = [];
    const updated = allNodes.map((n) => {
      const es = n.data.entity.eventSubscriptions || [];
      const se = n.data.entity.sourceEntities || [];

      const deletedEs = es.filter((sub: any) => sub.eventSourceId === deletedNodeId);
      const deletedSe = se.filter((sub: any) => sub.sourceEntityId === deletedNodeId);

      if (deletedEs.length > 0 || deletedSe.length > 0) {
        removedSubs.push(...deletedEs);
        return {
          ...n,
          data: {
            ...n.data,
            entity: {
              ...n.data.entity,
              eventSubscriptions: es.filter((sub: any) => sub.eventSourceId !== deletedNodeId),
              sourceEntities: se.filter((sub: any) => sub.sourceEntityId !== deletedNodeId),
            },
          },
        };
      }
      return n;
    });
    return { updated, removedSubs };
  }, []);

  const deleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => {
        const nodeToDelete = nds.find((n) => n.id === nodeId);

        // Also track all outgoing subscriptions from this node
        if (nodeToDelete?.data.entity.eventSubscriptions) {
          setDeletedSubscriptions((prev) => [...prev, ...nodeToDelete.data.entity.eventSubscriptions]);
        }

        const filteredNodes = nds.filter((n) => !n.id.startsWith(nodeId));
        const { updated: updatedNodes, removedSubs } = cleanupNodeReferences(filteredNodes, nodeId);

        setDeletedSubscriptions((prev) => [...prev, ...removedSubs]);

        const finalEdges = deriveEdges(updatedNodes);
        updateGraphState(updatedNodes, finalEdges, true);
        return updatedNodes;
      });
    },
    [setNodes, deriveEdges, updateGraphState, cleanupNodeReferences],
  );

  const onNodesDelete = useCallback(
    (deleted: Node<NodeData>[]) => {
      setNodes((nds) => {
        let updatedNodes = nds;
        const allRemovedSubs: any[] = [];
        deleted.forEach((d) => {
          // Track outgoing
          if (d.data.entity.eventSubscriptions) {
            allRemovedSubs.push(...d.data.entity.eventSubscriptions);
          }
          // Track incoming
          const { updated, removedSubs } = cleanupNodeReferences(updatedNodes, d.id);
          updatedNodes = updated;
          allRemovedSubs.push(...removedSubs);
        });

        setDeletedSubscriptions((prev) => [...prev, ...allRemovedSubs]);

        const finalEdges = deriveEdges(updatedNodes);
        updateGraphState(updatedNodes, finalEdges, true);
        return updatedNodes;
      });
    },
    [setNodes, deriveEdges, updateGraphState, cleanupNodeReferences],
  );

  const addNodesAndEdges = useCallback(
    async (newNodesToAdd: Node<NodeData>[]) => {
      newNodesToAdd.forEach(attachHandlers);

      setNodes((nds) => {
        const uniqueNewNodes = newNodesToAdd.filter((nn) => !nds.some((on) => on.id === nn.id));
        const allNodes = [...nds, ...uniqueNewNodes];
        const finalEdges = deriveEdges(allNodes);
        updateGraphState(allNodes, finalEdges, true);
        return allNodes;
      });
    },
    [setNodes, deriveEdges, updateGraphState],
  );

  const updateNode = useCallback(
    async (updatedEntity: any) => {
      const existingNodes = getNodes();
      const newNodes = [];

      const node = await createEntityNode(existingNodes, updatedEntity, maxHeight, viewOnly);
      newNodes.push(node);

      const findConnected = async (relations: any[], typeKey: string, defKey: string, idKey: string) => {
        for (const rel of relations) {
          if (!existingNodes.some((n) => n.id === rel[idKey])) {
            let connected;
            if (rel[typeKey] === EntityType.dataSourceInstance) {
              connected = await apiContext.getDataSourceInstance(rel[defKey], rel[idKey]);
            } else if (rel[typeKey] === EntityType.agentInstance) {
              connected = await apiContext.getAgentInstance(rel[defKey], rel[idKey]);
            } else if (rel[typeKey] === EntityType.userAppInstance) {
              connected = await apiContext.getUserAppInstance(rel[defKey], rel[idKey]);
            }
            if (connected) {
              newNodes.push(await createEntityNode(existingNodes, connected, maxHeight, viewOnly));
            }
          }
        }
      };

      await findConnected(
        updatedEntity.eventSubscriptions || [],
        'eventSourceType',
        'eventSourceDefId',
        'eventSourceId',
      );
      await findConnected(
        updatedEntity.sourceEntities || [],
        'sourceEntityType',
        'sourceEntityDefId',
        'sourceEntityId',
      );

      await addNodesAndEdges(newNodes);
    },
    [getNodes, maxHeight, viewOnly, apiContext, addNodesAndEdges],
  );

  useEffect(() => {
    handlersRef.current.updateNode = updateNode;
    handlersRef.current.deleteNode = deleteNode;
    handlersRef.current.getNewEntities = getNewEntities;
    handlersRef.current.getAllEntities = getAllEntities;
  }, [updateNode, deleteNode, getNewEntities, getAllEntities]);

  /**
   * Unified Data Synchronization (Fetch & Reload)
   */
  const displayPipeline = useCallback(
    async (pipeline: any) => {
      setPipelineInstanceId(pipeline.id);

      const newItems: Node<NodeData>[] = [];
      const currentNodes = getNodes();
      for (const item of pipeline.items || []) {
        const node = await createEntityNode(currentNodes, item, maxHeight, viewOnly);
        newItems.push(attachHandlers(node));
      }

      const finalEdges = deriveEdges(newItems);
      updateGraphState(newItems, finalEdges, false);
      setLayoutElements(true);
    },
    [maxHeight, viewOnly, getNodes, attachHandlers, deriveEdges, updateGraphState],
  );

  const handlePipelineChange = async (event: any) => {
    const selectedId = event.target.value;
    const selectedPipeline = loadedPipelines.find((p) => p.id === selectedId);
    if (!selectedPipeline) return;

    if (changed) {
      try {
        await confirm({
          description:
            'You have unsaved changes. Switching pipelines will discard these changes. Do you want to continue?',
          confirmationText: 'Switch Pipeline',
          cancellationText: 'Cancel',
          title: 'Unsaved Changes',
        });
      } catch {
        return; // Cancelled
      }
    }

    setActivePipeline(selectedPipeline);
    setChanged(false);
    await displayPipeline(selectedPipeline);
  };

  /**
   * Unified Data Synchronization (Fetch & Reload)
   */
  useEffect(() => {
    if (!isOpen || changed || isFetchingRef.current || lastFetchedIdRef.current === currentId) {
      if (!isOpen) lastFetchedIdRef.current = null;
      return;
    }

    const syncPipelineData = async () => {
      isFetchingRef.current = true;
      if (getNodes().length === 0) setLoading(true);

      try {
        if (pipelineInstanceIdParam) {
          // 4. Pipeline Instance ID Mode
          const pipeline = await apiContext.getPipelineInstance(pipelineInstanceIdParam);
          if (pipeline) {
            setLoadedPipelines([pipeline]);
            setActivePipeline(pipeline);
            await displayPipeline(pipeline);
          } else {
            setLoadedPipelines([]);
            setActivePipeline(null);
            updateGraphState([], [], false);
            toast({ message: 'Pipeline not found', type: 'error' });
          }
        } else if (surveyId && responseId) {
          // 2. Survey + Response Mode
          const pipeline = await apiContext.getUserSurveyProcessingResult(surveyId, responseId);
          if (pipeline) {
            setLoadedPipelines([pipeline]);
            setActivePipeline(pipeline);
            await displayPipeline(pipeline);
          } else {
            setLoadedPipelines([]);
            setActivePipeline(null);
            updateGraphState([], [], false);
            toast({ message: 'Pipeline not found', type: 'error' });
          }
        } else if (entity?.id) {
          // 1. Entity Mode
          const pipelines = await apiContext.getPipelineItems(entity.entityType, entity.entityDefId, entity.id);
          const list = Array.isArray(pipelines) ? pipelines : [];
          setLoadedPipelines(list);
          if (list.length > 0) {
            setActivePipeline(list[0]);
            await displayPipeline(list[0]);
          } else {
            setActivePipeline(null);
            const node = await createEntityNode([], entity, maxHeight, viewOnly);
            updateGraphState([attachHandlers(node)], [], false);
            setLayoutElements(true);
          }
        }
        lastFetchedIdRef.current = currentId;
      } catch (err) {
        setError(err);
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
      }
    };

    syncPipelineData();
  }, [
    isOpen,
    currentId,
    changed,
    entity,
    surveyId,
    responseId,
    pipelineInstanceIdParam,
    displayPipeline,
    updateGraphState,
    getNodes,
    navigate,
    apiContext,
  ]);

  const layoutApp = useCallback(async () => {
    const direction = isMobile ? 'TB' : 'LR';
    const layouted = getLayoutedElements(getNodes(), getEdges(), direction);
    updateGraphState(layouted.nodes, layouted.edges, false);

    setTimeout(() => {
      window.requestAnimationFrame(() => fitView({ duration: 800, padding: 0.2 }));
    }, 50);
  }, [getNodes, getEdges, fitView, updateGraphState, isMobile]);

  useEffect(() => {
    if (layoutElements) {
      const currentNodes = getNodes();
      const allMeasured =
        currentNodes.length > 0 &&
        currentNodes.every(
          (n) =>
            (n.width !== undefined && n.height !== undefined) ||
            (n.measured?.width !== undefined && n.measured?.height !== undefined),
        );

      if (nodesInitialized && allMeasured) {
        layoutApp();
        setLayoutElements(false);
      }
    }
  }, [nodesInitialized, layoutElements, layoutApp, getNodes]);

  /**
   * Entity Creation Helpers
   */
  const createEntity = useCallback(
    async (defId: string, instanceId?: string, type?: EntityType) => {
      let ent;
      switch (type) {
        case EntityType.agentInstance:
          ent = instanceId
            ? await apiContext.getAgentInstance(defId, instanceId)
            : await apiContext.getNewAgentInstance(defId);
          break;
        case EntityType.userAppInstance:
          ent = instanceId
            ? await apiContext.getUserAppInstance(defId, instanceId)
            : await apiContext.getNewUserAppInstance(defId);
          break;
        case EntityType.dataSourceInstance:
        default:
          ent = instanceId
            ? await apiContext.getDataSourceInstance(defId, instanceId)
            : await apiContext.getNewDataSourceInstance(defId);
          break;
      }
      const node = await createEntityNode(getNodes(), ent, maxHeight, viewOnly);
      await addNodesAndEdges([node]);
      setLayoutElements(true);
    },
    [apiContext, getNodes, maxHeight, viewOnly, addNodesAndEdges],
  );

  const handleCreateFromBlueprint = async (blueprintId: string) => {
    if (blueprintLoadedRef.current) return;
    blueprintLoadedRef.current = true;

    const blueprint = fullBlueprints.find((t) => t.id === blueprintId);
    if (!blueprint) {
      blueprintLoadedRef.current = false;
      return;
    }

    setLoadedBlueprintId(blueprintId);
    setLoading(true);

    try {
      const fetchedEntities: Array<{ tempId: string; entity: any }> = [];
      const promptSettingsList: BlueprintPromptSetting[] = [];

      const bpEntities = blueprint.entities || [];
      for (let i = 0; i < bpEntities.length; i++) {
        const tEntity = bpEntities[i];
        const newEntity = await (tEntity.entityType === EntityType.dataSourceInstance
          ? apiContext.getNewDataSourceInstance(tEntity.entityDefId)
          : tEntity.entityType === EntityType.agentInstance
            ? apiContext.getNewAgentInstance(tEntity.entityDefId)
            : apiContext.getNewUserAppInstance(tEntity.entityDefId));

        if (newEntity) {
          if (!newEntity.entityType) {
            newEntity.entityType = tEntity.entityType;
          }

          // Ensure settings object exists
          if (!newEntity.settings) {
            newEntity.settings = {};
          }

          // Merge blueprint settings if any exist
          if (tEntity.settings) {
            newEntity.settings = { ...newEntity.settings, ...tEntity.settings };
          }

          fetchedEntities.push({
            tempId: tEntity.id,
            entity: newEntity,
          });

          const entDef = newEntity.dataSourceDef || newEntity.agentDef || newEntity.userAppDef;
          const settingDefs = entDef?.settingDefs || [];

          // Flatten settingDefs in case it is section-based
          const flatSettings: any[] = [];
          settingDefs.forEach((secOrSetting: any) => {
            if (secOrSetting && Array.isArray(secOrSetting.items)) {
              flatSettings.push(...secOrSetting.items);
            } else if (secOrSetting) {
              flatSettings.push(secOrSetting);
            }
          });

          flatSettings.forEach((setting: any) => {
            if (!isItemVisible(setting, newEntity.settings)) {
              return;
            }

            const val = newEntity.settings[setting.name];
            const hasVal = val !== undefined && val !== null && val !== '';
            const hasDefault = setting.defaultValue !== undefined && setting.defaultValue !== null && setting.defaultValue !== '';
            
            if (setting.required && !hasVal && !hasDefault) {
              promptSettingsList.push({
                entityIndex: i,
                entityName: newEntity.name || entDef.name || 'Component',
                entityDefId: newEntity.entityDefId,
                setting: setting,
              });
            }
          });
        }
      }

      if (promptSettingsList.length > 0) {
        setPromptSettings(promptSettingsList);
        setPendingBlueprint(blueprint);
        setPendingEntities(fetchedEntities);
        setBlueprintPromptOpen(true);
      } else {
        await finalizeCreateFromBlueprint(blueprint, fetchedEntities);
      }
    } catch (err) {
      console.error('Failed to create pipeline from blueprint', err);
      blueprintLoadedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const finalizeCreateFromBlueprint = async (
    blueprint: any,
    entitiesList: Array<{ tempId: string; entity: any }>
  ) => {
    const newItems: Node<NodeData>[] = [];
    const entityIdMap: Record<string, string> = {};

    for (const item of entitiesList) {
      const node = await createEntityNode(getNodes(), item.entity, maxHeight, viewOnly);
      entityIdMap[item.tempId] = node.id;
      newItems.push(node);
    }

    const processSubs = (
      subs: any[],
      pushTo: 'eventSubscriptions' | 'sourceEntities',
      buildRel: (src: Node<NodeData>, tgt: Node<NodeData>, raw: any) => any,
    ) => {
      for (const raw of subs) {
        const srcNode = newItems.find((n) => n.id === entityIdMap[raw.source]);
        const tgtNode = newItems.find((n) => n.id === entityIdMap[raw.target]);
        if (srcNode && tgtNode) {
          if (!tgtNode.data.entity[pushTo]) tgtNode.data.entity[pushTo] = [];
          tgtNode.data.entity[pushTo].push(buildRel(srcNode, tgtNode, raw));
        }
      }
    };

    processSubs(blueprint.subscriptions || [], 'eventSubscriptions', (src, tgt, raw) => ({
      eventSourceType: src.data.entity.entityType,
      eventSourceDefId: src.data.entity.entityDefId,
      eventSourceId: src.data.entity.id || src.data.entity.nodeId,
      eventSubscriberType: tgt.data.entity.entityType,
      eventSubscriberDefId: tgt.data.entity.entityDefId,
      eventSubscriberId: tgt.data.entity.id || tgt.data.entity.nodeId,
      eventName: raw.eventName,
    }));

    processSubs(blueprint.targetEntities || [], 'sourceEntities', (src, tgt) => ({
      sourceEntityType: src.data.entity.entityType,
      sourceEntityDefId: src.data.entity.entityDefId,
      sourceEntityId: src.data.entity.id || src.data.entity.nodeId,
      targetEntityType: tgt.data.entity.entityType,
      targetEntityDefId: tgt.data.entity.entityDefId,
      targetEntityId: tgt.data.entity.id || tgt.data.entity.nodeId,
    }));

    await addNodesAndEdges(newItems);
    setLayoutElements(true);

    const tempPipeline = {
      id: '',
      tenantId: user?.tenantId || '',
      name: blueprint.name || 'Untitled Pipeline',
      description: blueprint.description || '',
      blueprintId: blueprint.id,
      items: newItems.map((n) => n.data.entity),
      createdDate: new Date(),
      createdBy: user?.identifier || '',
    };
    setLoadedPipelines([tempPipeline]);
    setActivePipeline(tempPipeline);
    setPipelineInstanceId(undefined);

    // Clear blueprintId from URL to prevent loading multiple times
    const params = new URLSearchParams(window.location.search);
    if (params.has('blueprintId')) {
      params.delete('blueprintId');
      navigate({ search: params.toString() }, { replace: true });
    }
  };

  const handleBlueprintSettingsSubmit = async (values: Record<number, Record<string, any>>) => {
    const updatedEntities = pendingEntities.map((item, index) => {
      const userVals = values[index];
      if (userVals) {
        return {
          ...item,
          entity: {
            ...item.entity,
            settings: {
              ...item.entity.settings,
              ...userVals,
            },
          },
        };
      }
      return item;
    });

    setBlueprintPromptOpen(false);
    await finalizeCreateFromBlueprint(pendingBlueprint, updatedEntities);
    setPromptSettings([]);
    setPendingBlueprint(null);
    setPendingEntities([]);
  };

  const handleBlueprintSettingsCancel = () => {
    setBlueprintPromptOpen(false);
    setPromptSettings([]);
    setPendingBlueprint(null);
    setPendingEntities([]);
    blueprintLoadedRef.current = false;

    if (blueprintIdParam) {
      navigate('/marketplace');
    }
  };

  useEffect(() => {
    if (blueprintIdParam && fullBlueprints.length > 0) {
      handleCreateFromBlueprint(blueprintIdParam);
    }
  }, [fullBlueprints, blueprintIdParam]);

  const onEdgeDelete = useCallback(
    (edgesToDelete: Edge[]) => {
      const allRemoved: any[] = [];
      setNodes((nds) => {
        let updatedNodes = nds;
        edgesToDelete.forEach((edge) => {
          const { source, target, data } = edge;
          updatedNodes = updatedNodes.map((node) => {
            if (node.id === target) {
              const entity = { ...node.data.entity };
              if (data?.eventSubscription) {
                const es = entity.eventSubscriptions || [];
                const removed = es.filter((sub: any) => sub.eventSourceId === source);
                allRemoved.push(...removed);

                entity.eventSubscriptions = es.filter((sub: any) => sub.eventSourceId !== source);
              }
              if (data?.sourceEntity) {
                entity.sourceEntities = (entity.sourceEntities || []).filter((se: any) => se.sourceEntityId !== source);
              }
              return { ...node, data: { ...node.data, entity } };
            }
            return node;
          });
        });
        return updatedNodes;
      });
      setDeletedSubscriptions((prev) => [...prev, ...allRemoved]);
      setChanged(true);
    },
    [setNodes],
  );

  const openSaveDialog = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveConfirm = async (name: string, description: string) => {
    try {
      setError(null);
      setLoading(true);

      const nodesToSave = nodes.map((n) => {
        const {
          id,
          nodeId,
          tenantId,
          name,
          description,
          entityType,
          entityDefId,
          settings,
          eventSubscriptions,
          sourceEntities,
        } = n.data.entity;
        return {
          id: id || nodeId,
          tenantId,
          name,
          description,
          entityType,
          entityDefId,
          settings,
          eventSubscriptions,
          sourceEntities,
        };
      });

      const res = await apiContext.submitPipeline({
        pipelineInstanceId: pipelineInstanceId,
        name: name || undefined,
        description: description || undefined,
        blueprintId: loadedBlueprintId || undefined,
        entities: nodesToSave,
        deletedEventSubscriptions: deletedSubscriptions,
      });

      const idToSave = res?.pipelineInstanceId || pipelineInstanceId;

      setActivePipeline((prev: any) => {
        if (prev) {
          return { ...prev, name, description };
        }
        return {
          id: idToSave || '',
          name,
          description,
          items: nodes.map((n) => n.data.entity),
        } as any;
      });

      toast({ message: 'Save successful' });
      setChanged(false);
      setDeletedSubscriptions([]);
      setSaveDialogOpen(false);

      // Re-fetch pipelines to update the active pipeline metadata & lists
      if (entity?.id) {
        const pipelines = await apiContext.getPipelineItems(entity.entityType, entity.entityDefId, entity.id);
        const list = Array.isArray(pipelines) ? pipelines : [];
        setLoadedPipelines(list);
        const savedPipeline = list.find((p) => p.id === idToSave);
        if (savedPipeline) {
          setActivePipeline(savedPipeline);
        }
      } else if (idToSave) {
        const savedPipeline = await apiContext.getPipelineInstance(idToSave);
        if (savedPipeline) {
          setLoadedPipelines([savedPipeline]);
          setActivePipeline(savedPipeline);
        }
      }

      if (!pipelineInstanceId && idToSave) {
        setPipelineInstanceId(idToSave);
        lastFetchedIdRef.current = idToSave;
        const params = new URLSearchParams(window.location.search);
        params.set('pipelineInstanceId', idToSave);
        params.delete('blueprintId');
        setSearchParams(params);
      } else {
        onConfigurationChange();
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const clearPipeline = useCallback(() => {
    confirm({
      description: 'Are you sure you want to clear the entire pipeline? This action cannot be undone.',
      confirmationText: 'Clear All',
      cancellationText: 'Cancel',
      title: 'Clear Pipeline',
    })
      .then(() => {
        // Collect all subscriptions to track them as deleted
        const allRemovedSubs: any[] = [];
        nodes.forEach((n) => {
          if (n.data.entity.eventSubscriptions) {
            allRemovedSubs.push(...n.data.entity.eventSubscriptions);
          }
        });

        setDeletedSubscriptions((prev) => [...prev, ...allRemovedSubs]);
        updateGraphState([], [], true);
        toast({ message: 'Pipeline cleared' });
      })
      .catch(() => {
        // Do nothing on cancel
      });
  }, [confirm, nodes, updateGraphState, toast]);

  const actionButtons = !viewOnly && (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Tooltip title="Fit view">
        <IconButton onClick={() => fitView()} size="small" sx={{ bgcolor: 'background.paper', boxShadow: 2 }}>
          <FitScreenIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Auto layout">
        <IconButton onClick={() => layoutApp()} size="small" sx={{ bgcolor: 'background.paper', boxShadow: 2 }}>
          <AccountTreeIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Clear pipeline">
        <IconButton
          onClick={clearPipeline}
          disabled={nodes.length === 0}
          size="small"
          sx={{ bgcolor: 'background.paper', boxShadow: 2, color: 'error.main' }}
        >
          <DeleteSweepIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('common.buttons.save')}>
        <span>
          <IconButton
            onClick={openSaveDialog}
            disabled={nodes.length === 0 || !changed}
            color="primary"
            size="small"
            sx={{ bgcolor: 'background.paper', boxShadow: 2 }}
          >
            <SaveIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={t('common.buttons.close')}>
        <span>
          {!entity && (
            <IconButton
              onClick={() => navigate('/dashboard')}
              color="primary"
              size="small"
              sx={{ bgcolor: 'background.paper', boxShadow: 2 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {error && <ErrorMessage error={error} />}
      {!viewOnly && !isMobile && (
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
          {actionButtons}
        </Box>
      )}

      {changed && (
        <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
          <Chip label="Unsaved changes" size="small" color="warning" sx={{ boxShadow: 2 }} />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, height: '100%', overflow: 'hidden' }}>
        {!viewOnly && (
          isMobile ? (
            <Drawer
              anchor="left"
              open={!leftSidebarCollapsed}
              onClose={() => setLeftSidebarCollapsed(true)}
              variant="temporary"
              PaperProps={{
                sx: { width: 280, height: '100%' }
              }}
            >
              <LeftSidebar
                blueprints={blueprints}
                onCreateFromBlueprint={handleCreateFromBlueprint}
                onCreateAgent={async (defId, instanceId) => {
                  await createEntity(defId, instanceId, EntityType.agentInstance);
                  setLeftSidebarCollapsed(true);
                }}
                onCreateUserApp={async (defId, instanceId) => {
                  await createEntity(defId, instanceId, EntityType.userAppInstance);
                  setLeftSidebarCollapsed(true);
                }}
                onCreateDataSource={async (defId, instanceId) => {
                  await createEntity(defId, instanceId, EntityType.dataSourceInstance);
                  setLeftSidebarCollapsed(true);
                }}
                collapsed={false}
                onToggleCollapse={() => setLeftSidebarCollapsed(true)}
              />
            </Drawer>
          ) : (
            <LeftSidebar
              blueprints={blueprints}
              onCreateFromBlueprint={handleCreateFromBlueprint}
              onCreateAgent={(defId, instanceId) => createEntity(defId, instanceId, EntityType.agentInstance)}
              onCreateUserApp={(defId, instanceId) => createEntity(defId, instanceId, EntityType.userAppInstance)}
              onCreateDataSource={(defId, instanceId) => createEntity(defId, instanceId, EntityType.dataSourceInstance)}
              collapsed={leftSidebarCollapsed}
              onToggleCollapse={() => setLeftSidebarCollapsed(!leftSidebarCollapsed)}
            />
          )
        )}

        <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
          {loading && <ProgressIndicator />}

          <PipelineInfoPanel
            loadedPipelines={loadedPipelines}
            activePipeline={activePipeline}
            viewOnly={viewOnly}
            onPipelineChange={handlePipelineChange}
            onEditClick={openSaveDialog}
            actionButtons={isMobile ? actionButtons : undefined}
          />

          {isMobile && leftSidebarCollapsed && !viewOnly && (
            <IconButton
              onClick={() => setLeftSidebarCollapsed(false)}
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                zIndex: 10,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: 3,
                width: 40,
                height: 40,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <ReactFlow
            nodes={nodes.map(attachHandlers)}
            edges={edges}
            nodeTypes={nodeTypesMap}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={() => {}}
            onNodesDelete={onNodesDelete}
            onEdgesDelete={onEdgeDelete}
            multiSelectionKeyCode={null}
            nodeOrigin={[0, 0]}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            style={{ width: '100%', height: '100%' }}
            preventScrolling={false}
            proOptions={{ hideAttribution: true }}
            fitView
          >
            <Controls />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </Box>
      </Box>

      <SavePipelineDialog
        open={saveDialogOpen}
        initialName={activePipeline?.name || ''}
        initialDescription={activePipeline?.description || ''}
        loading={loading}
        onCancel={() => setSaveDialogOpen(false)}
        onSave={handleSaveConfirm}
      />

      <BlueprintSettingsDialog
        open={blueprintPromptOpen}
        promptSettings={promptSettings}
        onCancel={handleBlueprintSettingsCancel}
        onSubmit={handleBlueprintSettingsSubmit}
      />
    </Box>
  );
};

const PipelineBuilder = ({
  maxHeight,
  entity,
  surveyId,
  responseId,
  isOpen,
  viewOnly = false,
  onConfigurationChange,
}: Props) => (
  <ReactFlowProvider>
    <ReactFlowPipelineBuilder
      maxHeight={maxHeight}
      entity={entity}
      surveyId={surveyId}
      responseId={responseId}
      isOpen={isOpen}
      viewOnly={viewOnly}
      onConfigurationChange={onConfigurationChange}
    />
  </ReactFlowProvider>
);

export default PipelineBuilder;
